import { Client } from "@hey-api/client-fetch";
import * as Sentry from "@sentry/react";

import { supabaseClient } from "@/config/supabase";
import { useMsalMigrationModal } from "@/stores/msal-migration-modal";
import { hasStaleMsalSession } from "@/utils/msal-migration";

import {
  clearFailureHistory,
  isSessionHealthAnomalous,
  recordRefreshSuccess401,
} from "./session-health-monitor";
import { getElapsedTime, getTokenAsync, getTokenSync } from "./token-cache";

interface InterceptorRequest extends Request {
  headers: Headers;
}

/**
 * Register HTTP interceptors for Supabase authentication.
 *
 * Request interceptor:
 *   - Attaches Supabase access token to requests (if not already present)
 *   - Uses fast sync cache when available, falls back to async fetch
 *
 * Response interceptor — 401 recovery strategy:
 *   - On 401: Attempt refreshSession() once (safe, non-blocking)
 *   - If refresh succeeds: Retry the request with new token
 *   - If retry also fails with 401: Record as anomaly (pattern detection)
 *   - On 3+ anomalies in 30s: Sign out (genuine session dead)
 *   - If refresh fails: Return error, let onAuthStateChange handle it
 *   - On 403: Log only (info level), never touch session
 *
 * Anomaly detection:
 *   - Tracks pattern: "refresh succeeds but request still fails"
 *   - Logs distinct Sentry events for observability:
 *     - "api_401_recovered": normal recovery (90% of cases)
 *     - "401_single_refresh_success": 1-2 failures (transient, 9%)
 *     - "401_loop_despite_refresh_success": 3+ failures (genuine issue, <1%)
 *     - "api_401_refresh_failed": refresh attempt failed
 *
 * @param client - The hey-api client to register interceptors on
 */
export const registerSupabaseHttpInterceptor = (client: Client) => {
  const requestInterceptor = async (
    request: InterceptorRequest,
  ): Promise<InterceptorRequest> => {
    const requestUrl = new URL(request.url);
    const endpoint = requestUrl.pathname;

    // If Authorization header already exists, use it as-is (no routing needed)
    if (request.headers.has("Authorization")) {
      return request;
    }

    // For Supabase users without Authorization header:
    // Try fast path first: get cached token (no network call, ~0.1ms)
    let token = getTokenSync();

    // If cache miss, attempt to get fresh session with retry for bad networks
    if (!token) {
      // Retry logic: up to 3 attempts with exponential backoff
      // Handles transient network issues (e.g., baseball field WiFi)
      let retryCount = 0;
      const maxRetries = 3;
      const delays = [0, 250, 1000]; // milliseconds: immediate, 250ms, 1s

      while (!token && retryCount < maxRetries) {
        if (retryCount > 0) {
          await new Promise((resolve) =>
            setTimeout(resolve, delays[retryCount]),
          );
        }
        token = await getTokenAsync(supabaseClient);
        retryCount++;
      }

      // If all retries exhausted and still no token, handle appropriately
      if (!token) {
        // Check if there's a stale MSAL session (migration scenario)
        if (hasStaleMsalSession()) {
          useMsalMigrationModal.getState().open();
          throw new Error(
            "No valid authentication session. Please re-login via the modal.",
          );
        }

        // No token after retries: throw network error
        console.error(
          `[${getElapsedTime()}] [Auth] Failed to acquire token after retries`,
          {
            endpoint,
          },
        );
        throw new Error(
          "Unable to authenticate user. Please check network connection and try again. If the problem persists, please refresh the page or logout and log back in.",
        );
      }
    }

    // Attach token if available
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
      return request;
    }

    return request;
  };

  const responseInterceptor = async (
    response: Response,
    request: InterceptorRequest,
  ): Promise<Response> => {
    const endpoint = new URL(request.url).pathname;
    const method = request.method;

    // ========== 401 HANDLER: Attempt Recovery ==========
    if (response.status === 401) {
      console.warn(
        `[${getElapsedTime()}] [Auth] 401 Unauthorized - Attempting recovery`,
        {
          endpoint,
          method,
        },
      );

      // Attempt to refresh the session
      const { data, error } = await supabaseClient.auth.refreshSession();

      // ========== DID REFRESH SUCCEED? ==========
      if (data?.session?.access_token) {
        // Update headers with new token
        request.headers.set(
          "Authorization",
          `Bearer ${data.session.access_token}`,
        );

        // Retry the request with new token using fetch
        const retryResponse = await fetch(request);

        // ========== DID RETRY ALSO FAIL WITH 401? (ANOMALY CASE) ==========
        if (retryResponse.status === 401) {
          recordRefreshSuccess401(endpoint, method);
          const isAnomalous = isSessionHealthAnomalous();

          console.error(
            `[${getElapsedTime()}] [Auth] Anomaly: 401 persists after token refresh`,
            {
              endpoint,
              is_anomalous: isAnomalous,
            },
          );

          const eventType = isAnomalous
            ? "401_loop_despite_refresh_success"
            : "401_single_refresh_success";

          Sentry.captureMessage(
            `[AUTH] 401 despite successful refresh - ${eventType}`,
            {
              level: isAnomalous ? "error" : "warning",
              tags: {
                component: "http-interceptor",
                event: eventType,
                endpoint,
                is_anomalous: isAnomalous ? "true" : "false",
              },
            },
          );

          // ========== SIGN OUT ONLY ON ANOMALY PATTERN ==========
          if (isAnomalous) {
            console.error(
              `[${getElapsedTime()}] [Auth] Session anomaly - signing out after repeated 401s`,
            );

            clearFailureHistory();
            await supabaseClient.auth.signOut();
          }

          return retryResponse;
        }

        // ========== RETRY SUCCEEDED, CLEAR ANOMALY COUNTER ==========
        if (retryResponse.ok) {
          clearFailureHistory();

          Sentry.captureMessage("[AUTH] 401 recovered via refresh and retry", {
            level: "info",
            tags: {
              component: "http-interceptor",
              event: "api_401_recovered",
              endpoint,
            },
          });
        }

        return retryResponse;
      }

      // ========== REFRESH ITSELF FAILED ==========
      console.error(
        `[${getElapsedTime()}] [Auth] Token refresh failed during 401 recovery`,
        {
          endpoint,
          error: error?.message,
        },
      );

      Sentry.captureMessage("[AUTH] Refresh failed during 401 recovery", {
        level: "warning",
        tags: {
          component: "http-interceptor",
          event: "api_401_refresh_failed",
          endpoint,
          error_code: error?.code || "unknown",
        },
      });

      return response;
    }

    // ========== 403 HANDLER: User lacks permission ==========
    if (response.status === 403) {
      console.warn(
        `%c[${getElapsedTime()}] [HTTP Interceptor] 403 Forbidden`,
        "color: #FF9800",
        {
          endpoint,
          method,
          message: "User has valid session but insufficient permissions",
        },
      );

      Sentry.captureMessage("[AUTH] 403 Forbidden - User lacks permission", {
        level: "info", // Not an error, normal for authorization checks
        tags: {
          component: "http-interceptor",
          event: "api_403",
          endpoint,
        },
        contexts: {
          request: {
            url: request.url,
            method: request.method,
            path: endpoint,
          },
        },
      });

      // ⚠️ CRITICAL: DO NOT refresh, DO NOT sign out
      // 403 means valid token, insufficient permission
      // This is NOT an auth failure
      return response;
    }

    return response;
  };

  client.interceptors.request.use(requestInterceptor);
  client.interceptors.response.use(responseInterceptor);
};
